import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import $ from 'jquery';

// ── jQuery type augmentation for turn.js ──────────────────────────────────
declare global {
  interface JQuery {
    turn(options: Record<string, unknown>): JQuery;
    turn(method: 'previous' | 'next' | 'destroy' | 'page', value?: number): JQuery;
  }
}

// turn.js is a jQuery plugin — it expects $ / jQuery on window before loading
const win = window as Window & { $?: JQueryStatic; jQuery?: JQueryStatic };
win.$ = $;
win.jQuery = $;

// ── turn.js loader (once per browser session) ─────────────────────────────
let turnLoaded = false;
let turnLoading: Promise<void> | null = null;

function loadTurnJs(): Promise<void> {
  if (turnLoaded) return Promise.resolve();
  if (turnLoading) return turnLoading;

  turnLoading = new Promise<void>((resolve, reject) => {
    // Already attached from a previous hot-reload cycle
    if (typeof ($.fn as any).turn === 'function') {
      turnLoaded = true;
      resolve();
      return;
    }

    // Remove any stale script tag that failed to attach the plugin
    const stale = document.querySelector<HTMLScriptElement>('script[data-turnjs]');
    if (stale) stale.remove();

    const script = document.createElement('script');
    script.src = '/turn.js';
    script.dataset.turnjs = '1';
    script.onload = () => {
      if (typeof ($.fn as any).turn === 'function') {
        turnLoaded = true;
        resolve();
      } else {
        reject(new Error('turn.js script loaded but $.fn.turn is not defined — check /public/turn.js'));
      }
    };
    script.onerror = () => reject(new Error('Failed to fetch /turn.js'));
    document.head.appendChild(script);
  });

  return turnLoading.catch((err) => {
    turnLoading = null;
    throw err;
  });
}

// ── Public types ──────────────────────────────────────────────────────────
export interface FlipbookHandle {
  prevPage: () => void;
  nextPage: () => void;
  goToPage: (page: number) => void;
}

/** Key = even (left) turn.js page number; value = 2× wide spread image URL */
export type FlipbookSpreads = Record<number, string>;

/**
 * A clickable "directory" ring drawn on top of a spread (e.g. the tile map),
 * jumping the book to `targetPage` when clicked.
 * x/y/w/h are percentages of the *page* box (0–100), matching the same
 * coordinate space as the spread-half image split (see buildSpreadHalf).
 */
export interface FlipbookHotspot {
  side: 'left' | 'right';
  xPct: number;
  yPct: number;
  wPct: number;
  hPct: number;
  targetPage: number;
  label?: string;
  /** Use a dark dashed ring (for tiles on light backgrounds). */
  darkRing?: boolean;
  /** Extra diameter multiplier on top of the global hotspot scale (1.13 = 13% larger). */
  sizeScale?: number;
  /** Draw as a horizontal oval instead of a circle. */
  oval?: boolean;
  /** Width/height stretch when oval (default 1.55). Greater = more elongated horizontally. */
  ovalStretchX?: number;
}

/** Key = the spread's left-page key (same key used in FlipbookSpreads). */
export type FlipbookHotspots = Record<number, FlipbookHotspot[]>;

interface FlipbookViewerProps {
  cacheKey: string;
  coverImage: string;
  spineColor: string;
  backCoverTitle: string;
  spreads?: FlipbookSpreads;
  spreadHotspots?: FlipbookHotspots;
  onPageChange: (page: number, total: number) => void;
}

// ── Book structure constants ───────────────────────────────────────────────
// Cover image native size: 4266 × 5367 px → aspect ratio width/height
const COVER_ASPECT = 4266 / 5367; // ≈ 0.7948

// turn.js hardcover page layout (must match the demo contract exactly):
//   page  1  – hard outer front cover
//   page  2  – hard inside front cover (blank)
//   pages 3…22 – 20 content pages (10 two-page spreads)
//   page 23  – hard inside back cover (blank)
//   page 24  – hard outer back cover
//
// In display:'double' + LTR:
//   page 1 shows alone on the right  (odd page → right side)
//   pages [2,3], [4,5], … are spreads
//   page 24 shows alone on the left  (when totalPages is even)
const DEFAULT_INNER_PAGES = 20; // 10 spreads for books without custom content

/** turn.js flip timing — default plugin value is 600 ms */
const FLIP_DURATION_MS = 400;
const FLIP_ELEVATION = 30;

// Spread images are pre-generated (see frontend/scripts/optimize-spreads.ps1) in
// two quality tiers living in sibling folders:
//   .../full/Spread-N.jpg   — higher quality, used once a page is at rest
//   .../thumb/Spread-N.jpg  — small + fast, used for the initial load + while flipping
// `spreads` (from Research.tsx) holds the "full" URL; the thumb URL is derived
// from it by convention so callers only need to list one path per spread.
function toThumbUrl(fullUrl: string): string {
  return fullUrl.includes('/full/') ? fullUrl.replace('/full/', '/thumb/') : fullUrl;
}

/** Thumb-tier URLs for every spread — small enough to gate initial readiness on. */
function uniqueThumbImages(spreads: FlipbookSpreads): string[] {
  return [...new Set(Object.values(spreads).map(toThumbUrl))];
}

/** Full-tier spread URLs — warmed in the background, swapped in per-page on flip. */
function uniqueFullImages(spreads: FlipbookSpreads): string[] {
  return [...new Set(Object.values(spreads))];
}

const PAGE_PAPER = '#f8f6f1';

const HOTSPOT_CLASS = 'flipbook-hotspot';
/** Uniform scale applied to every hotspot ring diameter (1.76 = 20% smaller than the prior 2.2). */
const HOTSPOT_SIZE_SCALE = 1.76;

/** Injects the hotspot ring CSS once per document (hover/focus states can't be inline). */
let hotspotStyleInjected = false;
function ensureHotspotStyle(): void {
  if (hotspotStyleInjected) return;
  hotspotStyleInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .${HOTSPOT_CLASS} {
      position: absolute;
      border: 2px dashed rgba(255,255,255,0.85);
      border-radius: 50%;
      aspect-ratio: 1;
      height: auto;
      background: rgba(255,255,255,0.04);
      cursor: pointer;
      padding: 0;
      box-sizing: border-box;
      box-shadow: 0 0 0 1px rgba(0,0,0,0.2);
      transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
      pointer-events: auto;
      z-index: 7;
    }
    .${HOTSPOT_CLASS}:hover, .${HOTSPOT_CLASS}:focus-visible {
      background-color: rgba(255,255,255,0.28);
      border-color: #ffffff;
      transform: scale(1.08);
      outline: none;
    }
    .${HOTSPOT_CLASS}--dark {
      border-color: rgba(0,0,0,0.75);
      background: rgba(0,0,0,0.04);
      box-shadow: 0 0 0 1px rgba(255,255,255,0.25);
    }
    .${HOTSPOT_CLASS}--dark:hover, .${HOTSPOT_CLASS}--dark:focus-visible {
      background-color: rgba(0,0,0,0.18);
      border-color: #000000;
    }
    .${HOTSPOT_CLASS}--oval {
      aspect-ratio: auto;
    }
    /* turn.js page wrappers default to overflow:hidden; allow near-fold
       oval hotspots to spill into the gutter without clipping. The spread
       image itself stays clipped inside its own overflow:hidden child. */
    .turn-page {
      overflow: visible !important;
    }
  `;
  document.head.appendChild(style);
}

/** Preload keys (thumb set, full set, or plain cover-only books) already warmed this session. */
const preloadedKeys = new Set<string>();

/** Warm cache and fully decode images before the user flips (avoids transparent frames). */
function preloadImages(urls: string[], cacheKey: string): Promise<void> {
  if (preloadedKeys.has(cacheKey)) return Promise.resolve();
  if (urls.length === 0) return Promise.resolve();
  return Promise.all(
    urls.map(
      (url) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          const afterLoad = () => {
            if (typeof img.decode === 'function') {
              img.decode().then(() => resolve()).catch(() => resolve());
            } else {
              resolve();
            }
          };
          img.onload = afterLoad;
          img.onerror = () => resolve();
          img.src = url;
        }),
    ),
  ).then(() => {
    preloadedKeys.add(cacheKey);
  });
}

/** Derive inner page count from spread map (left-page keys: 2, 4, 6, …). */
export function computeInnerPages(spreads: FlipbookSpreads = {}): number {
  const leftKeys = Object.keys(spreads)
    .map(Number)
    .filter((k) => k >= 2 && k % 2 === 0);
  if (leftKeys.length === 0) return DEFAULT_INNER_PAGES;
  const maxLeft = Math.max(...leftKeys);
  return maxLeft - 1; // pages 3…maxLeft+1
}

export function computeTotalPages(spreads: FlipbookSpreads = {}): number {
  return 4 + computeInnerPages(spreads);
}

/** @deprecated use computeTotalPages(spreads) — default book length only */
export const FLIPBOOK_TOTAL_PAGES = computeTotalPages();

type Dims = { pageW: number; pageH: number; flipW: number };

function calcDims(): Dims {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Base height mirrors the shelf covers: h-[calc(100dvh-448px)] max-h-[680px],
  // clamped to 280–680 px. Open flipbook is then scaled up ~15% so it reads
  // larger than the closed covers without changing the shelf layout.
  const shelfH = Math.min(Math.max(vh - 448, 280), 680);
  const openH = Math.min(Math.round(shelfH * 1.15), Math.round(680 * 1.15));

  // Try height-driven sizing first. Flipbook is 2 pages wide; arrows + padding
  // need ≈ 200 px of horizontal clearance.
  const pageWFromH = Math.floor(openH * COVER_ASPECT);
  if (pageWFromH * 2 <= vw - 200) {
    return { pageW: pageWFromH, pageH: openH, flipW: pageWFromH * 2 };
  }

  // Width-constrained fallback: derive height from available width
  const pageW = Math.floor((vw - 200) / 2);
  const pageH = Math.floor(pageW / COVER_ASPECT);
  return { pageW, pageH, flipW: pageW * 2 };
}

// ── HTML page builders ────────────────────────────────────────────────────
//
// CONTRACT with turn.js:
//   • The outer page div must have NO inline width / height / padding / margin.
//     turn.js calls $(page).css({ width: N, height: N }) during init, setting
//     those exactly.  Any pre-set value fights the plugin.
//   • The page div must fill its allocated slot visually.  We achieve this with
//     an inner wrapper using  position:absolute; inset:0  so the content is
//     anchored to the turn.js-controlled box, not to the content's natural size.
//     This prevents the "content jumps during fold" bug (which is caused by
//     flex/% layouts reflowing when turn.js moves the div into its fold layer).
//   • Cover image: override Tailwind preflight  img { height:auto; max-width:100% }
//     with inline  height:100%; max-width:none  (inline > type-selector specificity).

function buildFrontCover(coverImage: string): string {
  return `<div class="hard" style="overflow:hidden;position:relative;">
    <img src="${coverImage}" alt="cover" decoding="async"
      style="display:block;width:100%;height:100%;object-fit:cover;max-width:none;" />
  </div>`;
}

function buildHardBlank(bg: string): string {
  // Inside front / inside back — hard, blank, matching paper colour
  return `<div class="hard" style="background:${bg};position:relative;overflow:hidden;"></div>`;
}

/** Renders directory hotspot rings for one page (already filtered to that side). */
function buildHotspotsHtml(hotspots: FlipbookHotspot[]): string {
  return hotspots
    .map((h) => {
      // Circles use width% only (aspect-ratio:1). Scale diameter and keep the
      // circle centred on the original calibration box (xPct/yPct/wPct/hPct).
      // Ovals keep that vertical size and stretch width horizontally.
      const scale = HOTSPOT_SIZE_SCALE * (h.sizeScale ?? 1);
      const baseWPct = h.wPct * scale;
      const circleHeightPct = baseWPct * COVER_ASPECT;
      const stretchX = h.oval ? (h.ovalStretchX ?? 1.55) : 1;
      const wPct = baseWPct * stretchX;
      const hPct = circleHeightPct;
      // Anchor on the calibration-box centre so the oval stays over the tile.
      const centerX = h.xPct + h.wPct / 2;
      const centerY = h.yPct + h.hPct / 2;
      const leftPct = centerX - wPct / 2;
      const topPct = centerY - hPct / 2;
      const darkClass = h.darkRing ? ` ${HOTSPOT_CLASS}--dark` : '';
      const ovalClass = h.oval ? ` ${HOTSPOT_CLASS}--oval` : '';
      const heightStyle = h.oval ? `height:${hPct}%;` : '';
      return `<button type="button" class="${HOTSPOT_CLASS}${darkClass}${ovalClass}" data-hotspot-target="${h.targetPage}"
        aria-label="${h.label ?? `Go to page ${h.targetPage}`}"
        style="left:${leftPct}%;top:${topPct}%;width:${wPct}%;${heightStyle}"></button>`;
    })
    .join('');
}

// One 2× wide image split across a spread: left page shows the left half,
// right page shows the right half.  Inner inset:0 wrapper matches the cover
// contract so content stays pinned during turn.js fold animations.
//
// PROGRESSIVE QUALITY: the img initially loads the small "thumb" tier so pages
// render fast and flip smoothly. Once the page lands (turn.js "turned" event),
// upgradePage() swaps `src` to the "full" tier URL stashed in `data-full`.
// `data-page-num` lets that lookup find this element regardless of how turn.js
// has repositioned/wrapped it in the DOM.
//
// `hotspots` (already filtered to this side) are drawn as dashed rings inside
// the same non-scaled inset:0 wrapper as the image, so x%/y% coordinates line
// up with the visible page area regardless of the image's 200%-wide split.
// One 2× wide image split across a spread: left page shows the left half,
// right page shows the right half.  Inner inset:0 wrapper matches the cover
// contract so content stays pinned during turn.js fold animations.
//
// PROGRESSIVE QUALITY: the img initially loads the small "thumb" tier so pages
// render fast and flip smoothly. Once the page lands (turn.js "turned" event),
// upgradePage() swaps `src` to the "full" tier URL stashed in `data-full`.
// `data-page-num` lets that lookup find this element regardless of how turn.js
// has repositioned/wrapped it in the DOM.
//
// Image stays in an overflow:hidden layer (so the 200% split-crop works).
// Hotspots sit in a sibling overflow:visible layer so near-edge ovals can
// spill into the gutter instead of being chopped by the page box.
function buildSpreadHalf(
  spreadFullUrl: string,
  side: 'left' | 'right',
  pageNum: number,
  hotspots: FlipbookHotspot[] = [],
): string {
  const left = side === 'left' ? '0' : '-100%';
  const thumbUrl = toThumbUrl(spreadFullUrl);
  // Paper background prevents dark overlay showing through while turn.js
  // reparents the page DOM during a fold (before the img repaints).
  return `<div data-page-num="${pageNum}" style="overflow:visible;position:relative;background:${PAGE_PAPER};">
    <div style="position:absolute;top:0;left:0;right:0;bottom:0;overflow:hidden;background:${PAGE_PAPER};">
      <img src="${thumbUrl}" data-full="${spreadFullUrl}" alt=""
        style="display:block;position:absolute;top:0;left:${left};
               width:200%;height:100%;max-width:none;object-fit:fill;" />
    </div>
    <div style="position:absolute;top:0;left:0;right:0;bottom:0;overflow:visible;z-index:6;pointer-events:none;">
      ${buildHotspotsHtml(hotspots)}
    </div>
  </div>`;
}

/** Swap a spread page's <img> from thumb to full quality once it's decoded. */
function upgradePageToFull(root: HTMLElement, pageNum: number): void {
  if (pageNum < 1) return;
  const pageEl = root.querySelector<HTMLElement>(`[data-page-num="${pageNum}"]`);
  const img = pageEl?.querySelector<HTMLImageElement>('img[data-full]');
  if (!img || img.dataset.upgraded === '1') return;
  const fullUrl = img.dataset.full;
  if (!fullUrl) return;

  img.dataset.upgraded = '1'; // mark immediately so a second flip-back doesn't re-trigger
  const warm = new Image();
  warm.onload = () => {
    img.src = fullUrl;
  };
  warm.src = fullUrl;
}

function buildContentPage(turnJsPageNum: number, displayNum: number, pageH: number): string {
  const pad = Math.round(pageH * 0.07);
  // Fixed pixel height for the image placeholder — no % so layout cannot reflow
  // when turn.js clones this node into the fold layer.
  const imgH = Math.round(pageH * 0.38);
  const gap = Math.round(pad * 0.5);

  // In LTR display:'double', odd turn.js pages sit on the RIGHT side;
  // even pages sit on the LEFT side.
  // Page numbers go bottom-right on right pages, bottom-left on left pages.
  const numSide = turnJsPageNum % 2 !== 0 ? 'right' : 'left';
  const numBottom = Math.round(pad * 0.55);
  const numInset = Math.round(pad * 0.55);

  return `<div style="overflow:hidden;position:relative;background:#f8f6f1;">
    <div style="position:absolute;top:0;left:0;right:0;bottom:0;
                padding:${pad}px;box-sizing:border-box;overflow:hidden;background:#f8f6f1;">
      <div style="height:${imgH}px;flex-shrink:0;
                  background:#f3f1ec;border:1.5px dashed #cdc9be;
                  display:flex;align-items:center;justify-content:center;
                  margin-bottom:${gap}px;
                  color:#c0bdb6;font-size:12px;font-family:sans-serif;">
        Image placeholder
      </div>
      <p style="margin:0;font-size:12px;color:#6b6860;
                line-height:1.65;font-family:Georgia,serif;">
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod
        tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam,
        quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
      </p>
      <span style="position:absolute;bottom:${numBottom}px;${numSide}:${numInset}px;
                   color:#b0ada6;font-size:10px;font-family:sans-serif;">
        ${displayNum}
      </span>
    </div>
  </div>`;
}

function buildBackCover(spineColor: string, backCoverTitle: string): string {
  return `<div class="hard" style="background:${spineColor};position:relative;overflow:hidden;">
    <span style="position:absolute;top:50%;left:50%;
                 transform:translate(-50%,-50%);
                 color:rgba(255,255,255,0.5);font-size:11px;
                 letter-spacing:0.14em;font-family:sans-serif;
                 text-transform:uppercase;text-align:center;
                 line-height:1.35;max-width:85%;">
      ${backCoverTitle}
    </span>
  </div>`;
}

function buildAllPages(
  coverImage: string,
  spineColor: string,
  backCoverTitle: string,
  pageH: number,
  spreads: FlipbookSpreads = {},
  spreadHotspots: FlipbookHotspots = {},
): string {
  const innerPages = computeInnerPages(spreads);
  const hasSpreads = Object.keys(spreads).length > 0;
  let html = '';

  // page 1: hard outer front cover
  html += buildFrontCover(coverImage);

  // page 2: inside front — spread left half, blank, or cream endpaper
  html += spreads[2]
    ? buildSpreadHalf(spreads[2], 'left', 2, (spreadHotspots[2] ?? []).filter((h) => h.side === 'left'))
    : buildHardBlank('#f4f2ed');

  // pages 3…(2 + innerPages): content spreads or placeholders
  for (let i = 0; i < innerPages; i++) {
    const turnJsPage = 3 + i;
    const displayNum = i + 1;
    const spreadLeftPage = turnJsPage % 2 === 0 ? turnJsPage : turnJsPage - 1;

    if (spreads[spreadLeftPage]) {
      const side = turnJsPage % 2 === 0 ? 'left' : 'right';
      const hotspotsForPage = (spreadHotspots[spreadLeftPage] ?? []).filter((h) => h.side === side);
      html += buildSpreadHalf(spreads[spreadLeftPage], side, turnJsPage, hotspotsForPage);
    } else if (hasSpreads) {
      html += buildHardBlank('#f8f6f1');
    } else {
      html += buildContentPage(turnJsPage, displayNum, pageH);
    }
  }

  // inside back + outer back cover
  html += buildHardBlank('#f4f2ed');
  html += buildBackCover(spineColor, backCoverTitle);

  return html;
}

// ── Component ─────────────────────────────────────────────────────────────
const FlipbookViewer = forwardRef<FlipbookHandle, FlipbookViewerProps>(
  ({ cacheKey, coverImage, spineColor, backCoverTitle, spreads = {}, spreadHotspots = {}, onPageChange }, ref) => {
    const spreadsKey = JSON.stringify(spreads);
    const hotspotsKey = JSON.stringify(spreadHotspots);
    const containerRef = useRef<HTMLDivElement>(null);
    const [ready, setReady] = useState(false);
    // dims drives the container's visual style; updated on resize for correct layout.
    const [dims, setDims] = useState<Dims>(calcDims);

    // Keep a stable ref to onPageChange so the init effect never needs it in deps.
    const onPageChangeRef = useRef(onPageChange);
    onPageChangeRef.current = onPageChange;

    // Arrow controls exposed to parent
    useImperativeHandle(ref, () => ({
      prevPage() {
        if (!ready || !containerRef.current) return;
        try { $(containerRef.current).turn('previous'); } catch { /* busy animating */ }
      },
      nextPage() {
        if (!ready || !containerRef.current) return;
        try { $(containerRef.current).turn('next'); } catch { /* busy animating */ }
      },
      goToPage(page: number) {
        if (!ready || !containerRef.current) return;
        let max = computeTotalPages(spreads);
        try {
          const pages = ($(containerRef.current) as any).turn('pages');
          if (typeof pages === 'number' && pages > 0) max = pages;
        } catch { /* ok */ }
        const clamped = Math.min(Math.max(Math.round(page), 1), max);
        try { $(containerRef.current).turn('page', clamped); } catch { /* busy animating */ }
      },
    }), [ready]);

    // Update container visual size on viewport resize.
    // Also tell turn.js about the new size so it doesn't desync with React.
    useEffect(() => {
      const onResize = () => {
        const d = calcDims();
        setDims(d);
        if (containerRef.current && $(containerRef.current).data('opts')) {
          try {
            // Resize the container imperatively first so turn.js reads the right value.
            containerRef.current.style.width  = `${d.flipW}px`;
            containerRef.current.style.height = `${d.pageH}px`;
            ($(containerRef.current) as any).turn('size', d.flipW, d.pageH);
          } catch { /* turn.js not ready yet */ }
        }
      };
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }, []);

    // Load turn.js and initialise whenever the book changes.
    //
    // KEY DESIGN DECISIONS:
    //  1. `el` is captured at the top of the effect and used in cleanup so React
    //     setting containerRef.current = null on unmount cannot skip destroy().
    //  2. dims are NOT in the dependency array — calcDims() is called fresh
    //     inside the effect so turn.js always sees the live viewport size.
    //  3. A requestAnimationFrame barrier is inserted after loadTurnJs() so that
    //     the browser has committed layout (including inline width/height) before
    //     turn.js reads $(el).width() / $(el).height() during init.  On first open
    //     the await already provides this delay; on second open (fast path where
    //     turn.js is already loaded) it would otherwise race and read 0x0.
    useEffect(() => {
      // Capture el immediately — React nulls containerRef.current on unmount
      // before the cleanup function runs, so we must snapshot it now.
      const el = containerRef.current;
      if (!el) return;

      let cancelled = false;
      const totalPages = computeTotalPages(spreads);

      // Gate readiness on the cover + thumb-tier spreads only — these are small
      // (tens of KB) so the book opens fast. Full-quality spreads are warmed in
      // the background afterwards and swapped in per-page as the user lands on
      // them (see upgradePageToFull / the `turned` handler below).
      const thumbUrls = [coverImage, ...uniqueThumbImages(spreads)];
      const preloadPromise = preloadImages(thumbUrls, cacheKey);
      const fullUrls = uniqueFullImages(spreads);

      (async () => {
        const d = calcDims();

        // Sync dims state so the container's React-managed inline style stays
        // in step before turn.js measures the element.
        setDims(d);
        el.style.width  = `${d.flipW}px`;
        el.style.height = `${d.pageH}px`;

        setReady(false);
        onPageChangeRef.current(1, 1);

        await Promise.all([loadTurnJs(), preloadPromise]);
        if (cancelled) return;

        // Wait for the browser to commit layout so turn.js reads correct sizes.
        // This is critical on the fast path (second open) when turn.js is already
        // loaded and the await above resolves synchronously.
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        if (cancelled) return;

        // Destroy any previous turn.js instance still attached to this node.
        // (e.g. if React reused the DOM element, or a previous init was incomplete)
        try {
          if ($(el).data('opts')) $(el).turn('destroy');
        } catch { /* not yet initialised — safe to ignore */ }

        // Re-apply size after destroy (destroy resets inline styles).
        el.style.width  = `${d.flipW}px`;
        el.style.height = `${d.pageH}px`;

        // Write page HTML into the container *before* calling turn().
        // turn.js reads children at init time; innerHTML is synchronous.
        ensureHotspotStyle();
        el.innerHTML = buildAllPages(coverImage, spineColor, backCoverTitle, d.pageH, spreads, spreadHotspots);

        // Initialise turn.js.
        //
        // GEOMETRY CONTRACT:
        //   width  must equal  flipW  (= 2 × pageW)  exactly.
        //   height must equal  pageH              exactly.
        //   turn.js reads these via $(el).width() / $(el).height() as well as
        //   the values we pass here.  The container div has position:relative
        //   and an inline width/height style — no other CSS sets its size.
        $(el).turn({
          width: d.flipW,
          height: d.pageH,
          display: 'double',
          duration: FLIP_DURATION_MS,
          gradients: true,
          acceleration: true,
          elevation: FLIP_ELEVATION,
          autoCenter: false,
          page: 1,
          when: {
            turned(_e: Event, page: number) {
              onPageChangeRef.current(page, totalPages);
              // Upgrade whichever pages just landed on-screen to full quality.
              // display:'double' pairs pages as [page-1, page] or [page, page+1].
              const pair = page % 2 ? [page - 1, page] : [page, page + 1];
              pair.forEach((p) => upgradePageToFull(el, p));
            },
          },
        });

        if (cancelled) {
          try { $(el).turn('destroy'); } catch { /* ok */ }
          return;
        }

        setReady(true);
        onPageChangeRef.current(1, totalPages);

        // Warm the full-quality tier in the background (not awaited) so that
        // by the time the user flips to a page, its full image is likely
        // already cached and the upgrade swap above is near-instant.
        preloadImages(fullUrls, `${cacheKey}::full`);
      })().catch((err) => {
        console.error('[FlipbookViewer] init failed:', err);
      });

      // Directory hotspots (e.g. the tile map) jump the book to another page.
      // Delegated on the container so it survives turn.js reparenting the
      // page DOM during folds.
      const onHotspotClick = (e: MouseEvent) => {
        const target = (e.target as HTMLElement).closest<HTMLElement>('[data-hotspot-target]');
        if (!target) return;
        e.preventDefault();
        e.stopPropagation();
        const targetPage = Number(target.dataset.hotspotTarget);
        if (!Number.isNaN(targetPage)) {
          try { $(el).turn('page', targetPage); } catch { /* busy animating */ }
        }
      };
      el.addEventListener('click', onHotspotClick);

      return () => {
        cancelled = true;
        el.removeEventListener('click', onHotspotClick);
        // Use the captured `el` — containerRef.current may already be null here.
        try { if ($(el).data('opts')) $(el).turn('destroy'); } catch { /* ok */ }
      };
    // Deps: only the book data.  dims are intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheKey, coverImage, spineColor, backCoverTitle, spreadsKey, hotspotsKey]);

    return (
      <div className="relative flex items-center justify-center">
        {/*
          This div is the turn.js root element.

          Rules that must NOT be violated:
            1. No width / height Tailwind classes — turn.js owns those (set via inline style).
            2. No CSS transforms on this element or any of its ancestors.
               turn.js uses offsetParent chain (C() function) to position the fold
               overlay; transforms break that calculation.
            3. No overflow:hidden — the fold animation renders a page outside this
               box temporarily (the diagonal "fwrapper" square).

          The inline style below sets the exact dimensions turn.js expects.
        */}
        <div
          ref={containerRef}
          style={{ position: 'relative', width: dims.flipW, height: dims.pageH }}
        />

        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 text-white/80 text-xs tracking-widest">
            Loading…
          </div>
        )}
      </div>
    );
  },
);

FlipbookViewer.displayName = 'FlipbookViewer';
export default FlipbookViewer;
